import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {
    fetchTickets,
    saveTicket,
    TicketsState,
    Ticket,
    StatoTicketTypes,
    PrioritaTicketTypes,
} from "./ticketingSlice";
import {UtenteLogged} from "../authentication/authenticationSlice";
import {ErrorStatusTypes} from "../common/errorsDeclarations";
import {theme} from "../theme";
import {PermissionTypes} from "components/authentication/permissionsGroups";
import {utenteAutorizzato} from "components/authentication/UtenteAutorizzato";
import TicketForm from "./ticket/TicketForm"

import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    Menu,
    MenuItem,
    List,
    ListItemButton,
    ListItemText,
    ListItem,
    Typography,
    Pagination,
    TextField,
    Select,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    Stack,
    Backdrop,
    Checkbox,
    CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ReplayIcon from "@mui/icons-material/Replay";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {useAutorizzazioneGruppi} from "../authentication/useAutorizzazioneGruppi";

const TicketsPage = () => {
    const dispatch = useAppDispatch();

    const tickets = useSelector<RootState, TicketsState>((state) => state.ticketsState.tickets);
    const utenteLogged = useSelector<RootState, UtenteLogged>((state) => state.authentication.utenteLogged);

    const [stato, setStato] = useState<string | undefined>();
    const [listaStati, setListaStati] = useState<string[]>([]);
    const toggleStato = (stato: string) => {
        setListaStati((prevLista) => {
            if (prevLista.includes(stato)) {
                // Se lo stato è già presente nell'array, lo rimuoviamo
                return prevLista.filter((stato_str) => stato_str !== stato);
            } else {
                // Se lo stato non è presente, lo aggiungiamo
                return [...prevLista, stato];
            }
        });
    };
    const [priorita, setPriorita] = useState<string | undefined>();
    const [ownerId, setOwnerId] = useState<number | undefined>();
    const [page, setPage] = useState<number>(1);
    const [numero_record, setNumeroRecord] = useState<number>(10);
    const [search, setSearch] = useState<string>("");
    const [refresh, setRefresh] = useState<boolean>(false);

    // Anchor menu per filtri
    const [anchorElStatoFilter, setAnchorElStatoFilter] = useState<null | HTMLElement>(null);
    const [anchorElPriorita, setAnchorElPriorita] = useState<null | HTMLElement>(null);

    const handleStatoFilterClose = () => {
        setAnchorElStatoFilter(null);
        // onClose(selectedValue);
    };

    // Fetch iniziale e filtri
    useEffect(() => {
        dispatch(
            fetchTickets({
                listaStati,
                owner_id: ownerId,
                priorita,
                numero_record,
                page,
                search,
            })
        );
        setRefresh(false);
    }, [stato, priorita, page, numero_record, search, refresh]);

    const handleRefresh = () => setRefresh(true);

    const handleNewTicket = () => {
        if (utenteLogged.id && utenteLogged.email) {
            const nuovoTicket: Ticket = {
                id: null,
                autore_id: utenteLogged.id,
                autore_descrizione: (utenteLogged.first_name) ? `${utenteLogged.first_name} ${utenteLogged.last_name}` : `${utenteLogged.username} ${utenteLogged.id}`,
                autore_email: utenteLogged.email,
                // stato: StatoTicketTypes.APERTO,
                // priorita: PrioritaTicketTypes.BASSA,
                // commenti: [],
                // data_apertura: new Date().toISOString(),
            };
            dispatch(saveTicket({ticketToSave: nuovoTicket}));
        }
    };

    const abilitatoTicketing = useAutorizzazioneGruppi([PermissionTypes.TICKETING_ASSISTENZA, PermissionTypes.TICKETING_UTENTI]);

    return (
        <Box>
            <Stack direction="row" justifyContent="flex-start" sx={{mb: 2}}>
                <Button startIcon={<ReplayIcon/>} onClick={handleRefresh}>
                    Aggiorna
                </Button>
                <Button startIcon={<AddCircleIcon/>} onClick={handleNewTicket}>
                    Nuovo Ticket
                </Button>
            </Stack>

            <Backdrop open={tickets.errorsStack?.status == ErrorStatusTypes.PENDING}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <List>
                <ListItemText
                    sx={{
                        backgroundColor: "#1b75b1",
                        color: "#ddd",
                        py: 1,
                        mb: 1,
                    }}
                >
                    <Grid container spacing={1} sx={{px: 2}}>
                        <Grid size={{xs: 1}}>
                            <Typography variant="h6">N.</Typography>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <Typography variant="h6">Descrizione</Typography>
                        </Grid>
                        <Grid size={{xs: 1}}>
                            <Stack direction="row" spacing={1}>
                                <Typography variant="h6">Priorità</Typography>
                                <IconButton sx={{
                                    color:
                                        priorita != undefined
                                            ? theme.coloriFiltro.attivo
                                            : "inherit",
                                }}
                                            onClick={(e) => setAnchorElPriorita(e.currentTarget)}>
                                    <FilterAltIcon/>
                                </IconButton>
                                <Menu
                                    anchorEl={anchorElPriorita}
                                    open={Boolean(anchorElPriorita)}
                                    onClose={() => setAnchorElPriorita(null)}
                                >
                                    {Object.entries(PrioritaTicketTypes).map(([key, value]) => (
                                        <MenuItem
                                            key={key}
                                            onClick={() => {
                                                setPriorita(key);
                                                setAnchorElPriorita(null);
                                            }}
                                            sx={{backgroundColor: priorita === key ? "#4aaa" : "inherit"}}
                                        >
                                            {value}
                                        </MenuItem>
                                    ))}
                                    <MenuItem
                                        onClick={() => {
                                            setPriorita(undefined);
                                            setAnchorElPriorita(null);
                                        }}
                                    >
                                        Tutti
                                    </MenuItem>
                                </Menu>
                            </Stack>
                        </Grid>
                        <Grid size={{xs: 1}}>
                            <Stack direction="row" spacing={1} justifyContent={"center"}>
                                <Typography variant="h6">Stato</Typography>
                                <IconButton sx={{
                                    color:
                                        listaStati.length > 0
                                            ? theme.coloriFiltro.attivo
                                            : "inherit",
                                }} onClick={(e) => setAnchorElStatoFilter(e.currentTarget)}>
                                    <FilterAltIcon/>
                                </IconButton>
                                <Box
                                    style={{display: "flex", justifyContent: "space-between"}}
                                >
                                    <Menu
                                        id="simple-menu"
                                        anchorEl={anchorElStatoFilter}
                                        keepMounted
                                        open={Boolean(anchorElStatoFilter)}
                                        onClose={handleStatoFilterClose}
                                    >

                                        <FormGroup sx={{px: 1}}>
                                            {Object.entries(StatoTicketTypes).map(([key, value]) => <FormControlLabel
                                                key={key}
                                                control={<Checkbox checked={listaStati.includes(key)}
                                                                   onChange={() => toggleStato(key)}
                                                                   inputProps={{'aria-label': 'controlled'}}/>}
                                                label={value}/>)}
                                        </FormGroup>
                                        <ButtonGroup sx={{mt: 1}}>
                                            <Button onClick={() => {
                                                setListaStati([]);
                                                setAnchorElStatoFilter(null);
                                                setRefresh(true)

                                            }}
                                                    variant="outlined"
                                            >
                                                Tutte
                                            </Button>

                                            <Button onClick={() => {
                                                setRefresh(true)
                                                handleStatoFilterClose()
                                            }}
                                                    variant="outlined"
                                            >
                                                Filtra
                                            </Button>
                                        </ButtonGroup>

                                    </Menu>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid size={{xs: 2}}>
                            <Grid container>
                                <Grid size={{xs: 12}}>
                                    <Typography variant="h6" align="center">
                                        Origine
                                    </Typography>
                                </Grid>
                                {/*<Grid size={{xs: 6}}>*/}
                                {/*    <Typography variant="h6" align="center">Applicativo</Typography>*/}
                                {/*</Grid>*/}
                                <Grid size={{xs: 6}}>
                                    <Typography variant="h6" align="center">Autore</Typography>
                                </Grid>
                                <Grid size={{xs: 6}}>
                                    <Typography variant="h6" align="center">Owner</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <Grid container>
                                <Grid size={{lg: 12}}>
                                    <Typography variant="h6" align="center">
                                        Date
                                    </Typography>
                                </Grid>
                                <Grid size={{xs: 3}}>
                                    <Typography variant="body1" align="center">Apertura</Typography>
                                </Grid>
                                <Grid size={{xs: 3}}>
                                    <Typography variant="body1" align="center">Presa in carico</Typography>
                                </Grid>
                                <Grid size={{xs: 3}}>
                                    <Typography variant="body1" align="center">Risoluzione</Typography>
                                </Grid>
                                <Grid size={{xs: 3}}>
                                    <Typography variant="body1" align="center">Chiusura</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{xs: 1}}>
                            <Typography variant="h6" textAlign={"center"}></Typography>
                        </Grid>

                    </Grid>
                </ListItemText>

                {tickets.results?.map((ticket) => (
                    <TicketForm
                        key={ticket.id}
                        ticket={ticket}
                        consentiModifica={abilitatoTicketing}
                    />
                ))}

                <ListItem sx={{justifyContent: "space-between", mt: 2}}>
                    <Pagination
                        count={tickets.num_pages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                    />
                    <Typography>Totale: {tickets.count}</Typography>
                    <FormControl sx={{width: "100px"}}>
                        <InputLabel>Record</InputLabel>
                        <Select
                            value={numero_record.toString()}
                            onChange={(e) => setNumeroRecord(Number(e.target.value))}
                            label="Record"
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </Select>
                    </FormControl>
                </ListItem>
            </List>
        </Box>
    );
};

export default TicketsPage;
